import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import {
  Modal,
  Stack,
  Text,
  ProgressIndicator,
  IconButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize
} from '@fluentui/react';

interface ISyncProgress {
  type: 'progress' | 'complete' | 'error';
  processed: number;
  total: number;
  progress: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  message?: string;
}

interface ISyncModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  syncType: 'artikel' | 'kreditoren' | 'mengeneinheiten' | 'lieferstatus';
  title: string;
}

export const SyncModal: React.FC<ISyncModalProps> = ({
  isOpen,
  onDismiss,
  syncType,
  title
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<ISyncProgress | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs für direkte DOM-Updates (umgeht React Batching)
  const progressTextRef = useRef<HTMLDivElement>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);

  // Reset bei Modal-Öffnung
  useEffect(() => {
    if (isOpen) {
      setProgress(null);
      setLogs([]);
      setError(null);
      setIsRunning(false);
      setTimedOut(false);
    }
  }, [isOpen]);

  // Cleanup EventSource bei Unmount oder Modal-Schließung
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isOpen]);

  const startSync = () => {
    setIsRunning(true);
    setProgress(null);
    setLogs([]);
    setError(null);

    // Progress Bar initialisieren (direktes DOM)
    if (logsContainerRef.current) {
      logsContainerRef.current.style.width = '0%';
      logsContainerRef.current.textContent = '0%';
    }

    // Stats zurücksetzen
    const statCreated = document.getElementById('stat-created');
    const statUpdated = document.getElementById('stat-updated');
    const statSkipped = document.getElementById('stat-skipped');
    if (statCreated) statCreated.textContent = '0';
    if (statUpdated) statUpdated.textContent = '0';
    if (statSkipped) statSkipped.textContent = '0';

    // Progress-Text initialisieren
    if (progressTextRef.current) {
      progressTextRef.current.textContent = 'Verbinde...';
    }

    const now = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${now}] Synchronisation gestartet...`]);

    // Erstelle EventSource für Server-Sent Events
    // Production: relative URL, Development: localhost:3000 (umgeht webpack-dev-server Proxy Buffering)
    const baseUrl = process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:3000';
    const endpoint = `${baseUrl}/api/sync/${syncType}?stream=true`;
    const eventSource = new EventSource(endpoint, { withCredentials: true });
    eventSourceRef.current = eventSource;

    // Timeout: nach 5 Minuten ohne complete-Event → Verbindung trennen, Hinweis zeigen
    timeoutRef.current = setTimeout(() => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setTimedOut(true);
      setIsRunning(false);
    }, 5 * 60 * 1000);

    eventSource.onmessage = (event) => {
      try {
        const data: ISyncProgress = JSON.parse(event.data);

        // DIREKTE DOM-Updates (umgeht React Batching komplett!)
        const timestamp = new Date().toLocaleTimeString();

        // Update Progress-Text (direkt im DOM)
        if (progressTextRef.current) {
          if (data.message) {
            progressTextRef.current.textContent = data.message;
          } else if (data.total > 0) {
            const text = `${data.processed}/${data.total} (${data.progress}%) - Erstellt: ${data.created}, Aktualisiert: ${data.updated}, Übersprungen: ${data.skipped}`;
            progressTextRef.current.textContent = text;
          }
        }

        // Update Progress Bar (direkt im DOM)
        if (logsContainerRef.current && data.type === 'progress') {
          // Progress Bar Width aktualisieren
          logsContainerRef.current.style.width = `${data.progress}%`;
          logsContainerRef.current.textContent = `${data.progress}%`;
        }

        // Update Stats (direkt im DOM)
        if (data.type === 'progress') {
          const statCreated = document.getElementById('stat-created');
          const statUpdated = document.getElementById('stat-updated');
          const statSkipped = document.getElementById('stat-skipped');

          if (statCreated) statCreated.textContent = String(data.created);
          if (statUpdated) statUpdated.textContent = String(data.updated);
          if (statSkipped) statSkipped.textContent = String(data.skipped);
        }

        // Update React State nur für wichtige Zustandsänderungen
        if (data.type === 'complete') {
          if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
          setProgress(data);
          setLogs(prev => [
            ...prev,
            `[${timestamp}] ✓ Synchronisation abgeschlossen: ${data.created} erstellt, ${data.updated} aktualisiert, ${data.skipped} übersprungen`
          ]);
          setIsRunning(false);
          eventSource.close();
        } else if (data.type === 'error') {
          if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
          setError(data.message || 'Unbekannter Fehler');
          setLogs(prev => [
            ...prev,
            `[${timestamp}] ✗ Fehler: ${data.message}`
          ]);
          setIsRunning(false);
          eventSource.close();
        } else {
          // Update progress state (für ProgressIndicator)
          setProgress(data);
        }
      } catch (err) {
        console.error('Fehler beim Parsen der SSE-Daten:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('EventSource Fehler:', err);
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      setTimedOut(true);
      setIsRunning(false);
      eventSource.close();
    };
  };

  const handleDismiss = () => {
    // Schließe EventSource wenn noch aktiv
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsRunning(false);
    onDismiss();
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleDismiss}
      isBlocking={isRunning}
      containerClassName="sync-modal"
    >
      <Stack
        styles={{
          root: {
            padding: 24,
            minWidth: 600,
            maxWidth: 800
          }
        }}
        tokens={{ childrenGap: 16 }}
      >
        {/* Header */}
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
            {title}
          </Text>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            ariaLabel="Schließen"
            onClick={handleDismiss}
          />
        </Stack>

        {/* Error Message */}
        {error && (
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
            {error}
          </MessageBar>
        )}

        {/* Timeout / Verbindungsabbruch Message */}
        {timedOut && !error && (
          <MessageBar messageBarType={MessageBarType.warning}>
            Die Verbindung wurde unterbrochen. Der Sync läuft möglicherweise noch im Hintergrund.
            Bitte prüfe das Sync-Protokoll weiter unten für das Ergebnis.
          </MessageBar>
        )}

        {/* Start Button */}
        {!isRunning && !progress && (
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <button
              onClick={startSync}
              style={{
                padding: '10px 20px',
                fontSize: 16,
                backgroundColor: '#0078d4',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              Synchronisation starten
            </button>
          </Stack>
        )}

        {/* Progress Indicator - Zeige während Sync läuft */}
        {isRunning && (
          <Stack tokens={{ childrenGap: 12 }}>
            {progress && (
              <ProgressIndicator
                label={`Fortschritt: ${progress.processed} von ${progress.total}`}
                percentComplete={progress.progress / 100}
              />
            )}

            {/* Live-Update Text (direktes DOM) - IMMER sichtbar während Sync */}
            <div
              ref={progressTextRef}
              style={{
                padding: '8px 12px',
                backgroundColor: '#f3f2f1',
                borderRadius: 4,
                fontSize: 14,
                fontFamily: 'Consolas, monospace',
                minHeight: 40,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Initialisiere...
            </div>

            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
              <Spinner size={SpinnerSize.small} />
              <Text>Synchronisierung läuft...</Text>
            </Stack>
          </Stack>
        )}

        {/* Success Message */}
        {progress && progress.type === 'complete' && (
          <MessageBar messageBarType={MessageBarType.success}>
            Synchronisation erfolgreich abgeschlossen: {progress.created} erstellt, {progress.updated} aktualisiert, {progress.skipped} übersprungen
          </MessageBar>
        )}

        {/* Visual Progress Bar (Live-Update via direktes DOM) - Zeige während Sync UND nach Abschluss */}
        {(isRunning || (progress && progress.type === 'complete')) && (
          <div style={{ marginTop: 16 }}>
            {/* Progress Bar Container */}
            <div
              style={{
                width: '100%',
                height: 30,
                backgroundColor: '#e0e0e0',
                borderRadius: 15,
                overflow: 'hidden',
                marginBottom: 16
              }}
            >
              <div
                ref={logsContainerRef}
                style={{
                  height: '100%',
                  background: 'linear-gradient(90deg, #0078d4, #00a8e8)',
                  transition: 'width 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: 14,
                  width: '0%'
                }}
              >
                0%
              </div>
            </div>

            {/* Stats Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 12,
                marginTop: 12
              }}
            >
              <div
                style={{
                  backgroundColor: '#f3f2f1',
                  padding: 12,
                  borderRadius: 4,
                  textAlign: 'center'
                }}
              >
                <div
                  id="stat-created"
                  style={{
                    fontSize: 24,
                    fontWeight: 'bold',
                    color: '#107c10'
                  }}
                >
                  0
                </div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Erstellt</div>
              </div>
              <div
                style={{
                  backgroundColor: '#f3f2f1',
                  padding: 12,
                  borderRadius: 4,
                  textAlign: 'center'
                }}
              >
                <div
                  id="stat-updated"
                  style={{
                    fontSize: 24,
                    fontWeight: 'bold',
                    color: '#0078d4'
                  }}
                >
                  0
                </div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Aktualisiert</div>
              </div>
              <div
                style={{
                  backgroundColor: '#f3f2f1',
                  padding: 12,
                  borderRadius: 4,
                  textAlign: 'center'
                }}
              >
                <div
                  id="stat-skipped"
                  style={{
                    fontSize: 24,
                    fontWeight: 'bold',
                    color: '#797775'
                  }}
                >
                  0
                </div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Übersprungen</div>
              </div>
            </div>
          </div>
        )}

        {/* Logs (Final nach Abschluss) */}
        {!isRunning && logs.length > 0 && (
          <Stack
            styles={{
              root: {
                maxHeight: 300,
                overflowY: 'auto',
                padding: 12,
                backgroundColor: '#f3f2f1',
                borderRadius: 4,
                fontFamily: 'Consolas, monospace',
                fontSize: 12
              }
            }}
            tokens={{ childrenGap: 4 }}
          >
            <Text variant="medium" styles={{ root: { fontWeight: 600, marginBottom: 8 } }}>
              Protokoll
            </Text>
            {logs.map((log, index) => (
              <Text key={index} styles={{ root: { fontSize: 12 } }}>
                {log}
              </Text>
            ))}
          </Stack>
        )}

        {/* Close Button */}
        {!isRunning && (progress || timedOut || error) && (
          <Stack horizontal horizontalAlign="end">
            <button
              onClick={handleDismiss}
              style={{
                padding: '8px 16px',
                fontSize: 14,
                backgroundColor: '#f3f2f1',
                color: '#323130',
                border: '1px solid #8a8886',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              Schließen
            </button>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};
